import { lazy, Suspense } from 'react';
import type { ReactNode } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import type { Capability } from '@shared/permissions';
import { useAuth } from '@/lib/auth';
import { AppLayout } from '@/components/AppLayout';
import { EmptyState } from '@/components/EmptyState';
import { LoadingScreen } from '@/components/LoadingSkeleton';
import { RouteErrorBoundary } from '@/components/RouteErrorBoundary';

/* Lazy page modules (code-split per page). Files marked STUB today are
 * replaced by the page agent; the default-export names below are the
 * contract and must not change. */
const LoginPage = lazy(() => import('@/pages/LoginPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/ResetPasswordPage'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage'));
const StudentsPage = lazy(() => import('@/pages/StudentsPage'));
const StudentFormPage = lazy(() => import('@/pages/StudentFormPage'));
const StudentDetailPage = lazy(() => import('@/pages/StudentDetailPage'));
const FeesStructurePage = lazy(() => import('@/pages/FeesStructurePage'));
const PaymentsPage = lazy(() => import('@/pages/PaymentsPage'));
const PaymentFormPage = lazy(() => import('@/pages/PaymentFormPage'));
const ReceiptPage = lazy(() => import('@/pages/ReceiptPage'));
const InvoiceViewPage = lazy(() => import('@/pages/InvoiceViewPage'));
const ReportsPage = lazy(() => import('@/pages/ReportsPage'));
const ReportViewPage = lazy(() => import('@/pages/ReportViewPage'));
const UsersPage = lazy(() => import('@/pages/UsersPage'));
const ApprovalsPage = lazy(() => import('@/pages/ApprovalsPage'));
const AccountsPage = lazy(() => import('@/pages/AccountsPage'));
const StudentAccountPage = lazy(() => import('@/pages/StudentAccountPage'));
const AuditLogPage = lazy(() => import('@/pages/AuditLogPage'));
const SettingsPage = lazy(() => import('@/pages/SettingsPage'));
const BackupsPage = lazy(() => import('@/pages/BackupsPage'));
const YearEndPage = lazy(() => import('@/pages/YearEndPage'));
const ChangePasswordPage = lazy(() => import('@/pages/ChangePasswordPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

/** Redirects anonymous users to /login, remembering where they were headed. */
export function ProtectedRoute() {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingScreen label="Loading your session" />;
  if (status === 'anonymous') {
    const intended = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ from: intended }} />;
  }
  return <Outlet />;
}

/** Renders children only when the session grants the capability. */
export function CapabilityRoute({
  capability,
  children,
}: {
  capability: Capability;
  children: ReactNode;
}) {
  const { status, can } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <LoadingScreen label="Checking your access" />;
  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  if (!can(capability)) {
    return (
      <EmptyState
        icon={<ShieldAlert className="h-6 w-6" />}
        title="You do not have access to this area"
        message="Your account role does not include permission for this page. Ask an administrator if you believe you need access."
      />
    );
  }
  return <>{children}</>;
}

export default function App() {
  const location = useLocation();

  return (
    <RouteErrorBoundary resetKey={location.pathname}>
      <Suspense fallback={<LoadingScreen label="Loading page" />}>
        <Routes>
          {/* Public authentication screens */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Authenticated application shell */}
          <Route element={<ProtectedRoute />}>
            <Route element={<AppLayout />}>
              <Route index element={<DashboardPage />} />
              <Route path="students" element={<StudentsPage />} />
              <Route path="students/new" element={<StudentFormPage />} />
              <Route path="students/:id" element={<StudentDetailPage />} />
              <Route path="students/:id/edit" element={<StudentFormPage />} />
              <Route path="fees-structure" element={<FeesStructurePage />} />
              <Route path="payments" element={<PaymentsPage />} />
              <Route path="payments/new" element={<PaymentFormPage />} />
              <Route path="receipts/:id" element={<ReceiptPage />} />
              <Route path="invoices/:id" element={<InvoiceViewPage />} />
              <Route path="reports" element={<ReportsPage />} />
              <Route path="reports/:name" element={<ReportViewPage />} />
              <Route
                path="users"
                element={
                  <CapabilityRoute capability="user:view">
                    <UsersPage />
                  </CapabilityRoute>
                }
              />
              <Route
                path="approvals"
                element={
                  <CapabilityRoute capability="approval:view">
                    <ApprovalsPage />
                  </CapabilityRoute>
                }
              />
              <Route path="accounts" element={<AccountsPage />} />
              <Route path="accounts/:studentId" element={<StudentAccountPage />} />
              <Route
                path="audit"
                element={
                  <CapabilityRoute capability="audit:view">
                    <AuditLogPage />
                  </CapabilityRoute>
                }
              />
              <Route
                path="settings"
                element={
                  <CapabilityRoute capability="settings:view">
                    <SettingsPage />
                  </CapabilityRoute>
                }
              />
              <Route
                path="backups"
                element={
                  <CapabilityRoute capability="backup:view">
                    <BackupsPage />
                  </CapabilityRoute>
                }
              />
              <Route
                path="year-end"
                element={
                  <CapabilityRoute capability="year:close">
                    <YearEndPage />
                  </CapabilityRoute>
                }
              />
              <Route path="change-password" element={<ChangePasswordPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </RouteErrorBoundary>
  );
}
